import { LocationApiService, PropertyApiService } from '@admin/core';
import { HealthStatus, PropertyEntity, TagStatus } from '@admin/core/models';
import { PropertyPageMode } from '@admin/dialog/components/property-dialog/property-dialog.component';
import { HealthValues, TagStatusValues } from '@admin/shared/data';
import { Component, inject, input, OnInit } from '@angular/core';
import { CacTableComponent, ItemRecord, PermissionService, table, TableAction, TableClass } from '@controladad/ng-base';
import { BehaviorSubject, switchMap, take } from 'rxjs';
import { ImportComponent } from '../import/import.component';
import { personnelArrayToString } from '@admin/shared/helpers';

@Component({
  selector: 'app-property-list',
  imports: [CacTableComponent, ImportComponent],
  templateUrl: './property-list.component.html',
})
export class PropertyListComponent implements OnInit {
  mode = input.required<PropertyPageMode>();

  private readonly propertyApi = inject(PropertyApiService);
  private readonly locationApi = inject(LocationApiService);
  private readonly permission = inject(PermissionService);
  

  locations$ = new BehaviorSubject<ItemRecord<number>[]>([]);
  table?: TableClass<PropertyEntity>;

  constructor() {
    this.locationApi
      .getLocationItems()
      .pipe(take(1))
      .subscribe((locations) => {
        this.locations$.next(locations);
      });
  }

  ngOnInit(): void {
    this.table = table({
      itemsFn:
        this.mode() === 'property-tag' ? this.propertyApi.getPropertyTagsAdapter() : this.propertyApi.getAdapter(),
      view: {
        title: this.mode() === 'property' ? 'لیست دارایی ' : 'لیست دارایی-تگ',
        itemName: 'دارایی',
        permission: {
          export: 'ExportPropertyTags',
        },
      },
      pagination: {
        size: 10,
      },
      export: this.mode() === 'property-tag' ? (options) => this.propertyApi.exportPropertyTags(options) : undefined,
      columns: {
        name: { label: 'نام', filterable: true },
        code: { label: 'کد', filterable: true },
        tagSerial: { label: 'سریال تگ', filterable: true },
        locationName: {
          label: 'مکان',
          filterable: [
            {
              type: 'equal',
              controlType: 'select',
              items: this.locations$,
              multiple: true,
              key: 'LocationId',
            },
          ],
        },
        personnel: {
          label: 'تحویل گیرنده',
          filterable: 'PersonnelSearch',
          transform: (v) => personnelArrayToString(v),
        },
        healthStatus: {
          label: 'وضعیت سلامت',
          items: HealthValues,
          filterable: true,
          type: 'tag',
          dynamicClass: (v) => (v === HealthStatus.Healthy ? 'text-green-700' : 'text-error-600'),
          hide: this.mode() === 'property-tag',
        },
        tagStatus: {
          label: 'وضعیت تگ',
          items: TagStatusValues,
          type: 'tag',
          filterable: [
            {
              type: 'equal',
              controlType: 'select',
              items: TagStatusValues,
              multiple: true,
            },
          ],
          dynamicClass: (v) =>
            v === TagStatus.HasTag
              ? 'text-green-700'
              : v === TagStatus.PendingAttachment || v === TagStatus.PendingApproval
                ? 'text-yellow-700'
                : 'text-error-600',
          hide: this.mode() === 'property',
        },
      },
      actions: [
        ...(this.mode() === 'property'
          ? [
            {
              type: 'button',
              content: 'تاریخچه',
              action: (item: PropertyEntity) => {
                dialog$.propertyHistory(item.id);
              },
            },
          ]
          : []),
        {
          type: 'button',
          content: 'مشاهده جزییات',
          class: 'w-36',
          action: (item) =>
            this.propertyApi
              .getProperty(item.id)
              .pipe(switchMap((details) => dialog$.property({ mode: this.mode(), property: details }).afterSubmit())),
        },
      ] as TableAction<PropertyEntity>[],
      bulkActions:
        this.mode() === 'property-tag' && this.permission.hasPermission('EditPropertyTags')
          ? [
            {
              content: 'تغییر وضعیت تگ',
              action: (items) =>
                dialog$
                  .tagStatus(items)
                  .afterSubmit()
                  .pipe(
                    switchMap((status: TagStatus) =>
                      this.propertyApi.bulkUpdatePropertyTags(
                        items.map((x) => ({ id: x.id, tagStatus: { value: status } })),
                      ),
                    )
                  ),

            },
          ]
          : [],
    });
  }

  onRefresh() {
    this.table?.refresh();
  }
}
